import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Policy } from '../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { runPolicies, UserKeys } from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { useStore } from '../state/zustand-store';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { Messages } from '../../utils/message-constants';
import ChangeLogDialog from '../../shared/change-log-dialog';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import SharedDialog from '../../shared/shared-dialog';

import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

interface RunPoliciesDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedIds: number[];
  setSelectedIds: Dispatch<SetStateAction<number[]>>;
  policyList: Policy[];
  changeLogRoles: string[];
}

export default function RunPoliciesDialog({
  open,
  handleClose,
  selectedIds,
  setSelectedIds,
  policyList,
  changeLogRoles,
}: Readonly<RunPoliciesDialogProps>) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const [openChangeLog, setOpenChangeLog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { selectedPolicies, runningPolicies, policiesToRun } = useMemo(() => {
    const selected = policyList.filter((p) => selectedIds.includes(p.id));
    return {
      selectedPolicies: selected,
      runningPolicies: selected.filter((p) => p.status === 'RUNNING'),
      policiesToRun: selected.filter((p) => p.status !== 'RUNNING'),
    };
  }, [policyList, selectedIds]);
  const allRunning = policiesToRun.length === 0;

  const { mutate, isPending } = useMutation({
    mutationFn: ({ ids, msg }: { ids: number[]; msg: string }) =>
      runPolicies(ids, msg),
    onMutate: ({ ids }) => {
      const previous = queryClient.getQueryData<Policy[]>([UserKeys.Policies]);
      queryClient.setQueryData<Policy[]>(
        [UserKeys.Policies],
        (old) =>
          old?.map((p) =>
            ids.includes(p.id) ? { ...p, status: 'RUNNING' as const } : p
          )
      );
      return { previous };
    },
    onSuccess: (returnData, { ids }) => {
      queryClient.setQueryData<Policy[]>([UserKeys.Policies], (oldData) => {
        if (!oldData) return;
        return oldData.map((policy) =>
          ids.includes(policy.id)
            ? { ...policy, lastRunDate: returnData }
            : policy
        );
      });
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          ids.length > 1
            ? 'The selected policies are now running!'
            : 'The policy is now running!',
      });
      setSelectedIds([]);
      handleClose();
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([UserKeys.Policies], context.previous);
      }
      handleErrors({
        error,
        snackbarMessage:
          policiesToRun.length > 1
            ? Messages.Errors.Network.Running('the selected policies')
            : Messages.Errors.Network.Running('this policy'),
      });
    },
    onSettled: () => {
      setOpenChangeLog(false);
      setChangeLogMessage('');
      createEvent(analytics.automation.policies.run_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.policies,
      });
    },
  });

  function handleRun() {
    const ids = policiesToRun.map((p) => p.id);
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.MaunalRunPolicy.value)
    ) {
      setOpenChangeLog(true);
    } else {
      mutate({ ids, msg: '' });
    }
  }

  function handleRunWithChangeLog() {
    const ids = policiesToRun.map((p) => p.id);
    mutate({ ids, msg: changeLogMessage });
  }

  const title = selectedPolicies.length > 1 ? 'Run Policies' : 'Run Policy';
  const runLabel = policiesToRun.length > 1 ? 'Run Policies' : 'Run Policy';

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={title}
        maxWidth="sm"
        dialogContent={
          <>
            {allRunning ? (
              <Typography>
                All selected policies are already running.
              </Typography>
            ) : policiesToRun.length === 1 ? (
              <>
                <Typography>
                  Run{' '}
                  <Typography component="span" fontWeight={700}>
                    {policiesToRun[0].policyName}
                  </Typography>
                  . This may take a few minutes depending on the data size.
                </Typography>
                {runningPolicies.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {runningPolicies.length === 1
                      ? `"${runningPolicies[0].policyName}" is already running and will be skipped.`
                      : `${runningPolicies.length} policies are already running and will be skipped.`}
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Typography>
                  Run{' '}
                  <Typography component="span" fontWeight={700}>
                    {policiesToRun.length} policies
                  </Typography>
                  ? This may take a few minutes depending on the data size.
                </Typography>
                {runningPolicies.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {runningPolicies.length === 1
                      ? `"${runningPolicies[0].policyName}" is already running and will be skipped.`
                      : `${runningPolicies.length} policies are already running and will be skipped.`}
                  </Alert>
                )}
              </>
            )}
          </>
        }
        primaryButton={{
          title: runLabel,
          action: handleRun,
          loading: isPending,
          disabled: isPending || allRunning,
        }}
      />
      {openChangeLog && (
        <ChangeLogDialog
          open={openChangeLog}
          handleClose={() => setOpenChangeLog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={handleRunWithChangeLog}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
